import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';

@Component({
  selector: 'app-three-sixty-viewer-core',
  templateUrl: './three-sixty-viewer-core.component.html',
  styleUrls: ['./three-sixty-viewer-core.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThreeSixtyViewerCoreComponent implements AfterViewInit {
  @Input() images: string[] = [];
  @Input() minimal = false;
  @Output() openEmitter = new EventEmitter<void>();

  @ViewChild('canvasRef', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('containerRef', { static: true }) containerRef!: ElementRef<HTMLElement>;

  loadedImages: HTMLImageElement[] = [];
  currentFrame = 0;
  frameCount = 0;
  sensitivity = 40;

  private startX: number | null = null;
  private accumulatedDelta = 0;
  private resizeDebounceTimer?: ReturnType<typeof setTimeout>;

  readonly radius = 16;
  readonly circumference = 2 * Math.PI * this.radius;
  progressOffset = this.circumference;

  isAutoplaying = false;
  private holdInterval?: any;
  private holdTimeout?: any;
  private hasHeld = false;

  private autoplayRafId: number | null = null;
  private lastAutoplayTime = 0;
  private autoplayInterval = 75;

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.frameCount = this.images.length;
    this.preloadInitial().then(() => {
      this.setupCanvas();
      this.drawFrame(0);
      this.attachEvents();
      this.lazyLoadRest();
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.resizeDebounceTimer) {
      clearTimeout(this.resizeDebounceTimer);
    }

    this.resizeDebounceTimer = setTimeout(() => {
      if (this.loadedImages[this.currentFrame]) {
        this.setupCanvas();
        this.drawFrame(this.currentFrame);
      }
    }, 300);
  }

  private preloadInitial(): Promise<void> {
    const initialBatch = this.images.slice(0, 6);
    return this.loadImages(initialBatch);
  }

  private lazyLoadRest(): void {
    const rest = this.images.slice(6);
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => this.loadImages(rest));
    } else {
      setTimeout(() => this.loadImages(rest), 100);
    }
  }

  private loadImages(urls: string[]): Promise<void> {
    return new Promise((resolve) => {
      let loaded = 0;
      urls.forEach((url) => {
        const img = new Image();
        img.onload = () => {
          const index = this.images.indexOf(url);
          this.loadedImages[index] = img;
          loaded++;
          if (loaded === urls.length) resolve();
        };
        img.src = url;
      });
    });
  }

  private setupCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    const container = this.containerRef.nativeElement;

    const aspectRatio = 4 / 3;
    const dpr = window.devicePixelRatio || 1;

    const containerWidth = container.clientWidth;
    let targetWidth = containerWidth - 40;
    let targetHeight = Math.round(targetWidth / aspectRatio);

    const maxHeight = Math.floor(window.innerHeight * 0.9);
    if (targetHeight > maxHeight) {
      targetHeight = maxHeight;
      targetWidth = Math.round(targetHeight * aspectRatio);
    }

    const physicalWidth = Math.floor(targetWidth * dpr);
    const physicalHeight = Math.floor(targetHeight * dpr);

    canvas.width = physicalWidth;
    canvas.height = physicalHeight;
    canvas.style.width = `${targetWidth}px`;
    canvas.style.height = `${targetHeight}px`;
    canvas.style.imageRendering = 'auto';

    const ctx = canvas.getContext('2d', {
      alpha: false,
      willReadFrequently: true,
    }) as CanvasRenderingContext2D | null;

    if (!ctx) return;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
  }

  private drawFrame(index: number): void {
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d', {
      alpha: false,
      willReadFrequently: true,
    }) as CanvasRenderingContext2D | null;
    const img = this.loadedImages[index];

    if (!ctx || !img) return;

    const displayWidth = parseInt(canvas.style.width, 10);
    const displayHeight = parseInt(canvas.style.height, 10);

    ctx.clearRect(0, 0, displayWidth, displayHeight);
    ctx.drawImage(img, 0, 0, displayWidth, displayHeight);
  }

  private attachEvents(): void {
    const canvas = this.canvasRef.nativeElement;

    const handleMove = (clientX: number) => {
      if (this.startX === null) return;

      const delta = clientX - this.startX;
      this.accumulatedDelta += delta;

      const frameShift = Math.floor(this.accumulatedDelta / this.sensitivity);
      if (frameShift !== 0) {
        this.currentFrame = (this.currentFrame + frameShift + this.frameCount) % this.frameCount;
        this.drawFrame(this.currentFrame);
        this.accumulatedDelta -= frameShift * this.sensitivity;
        this.startX = clientX;
      }
    };

    canvas.addEventListener('mousedown', (e) => {
      this.startX = e.clientX;
      this.stopAutoplay();
    });

    canvas.addEventListener('mousemove', (e) => handleMove(e.clientX));

    canvas.addEventListener('mouseup', () => {
      this.startX = null;
      this.accumulatedDelta = 0;
    });

    canvas.addEventListener('mouseleave', () => {
      this.startX = null;
      this.accumulatedDelta = 0;
    });

    canvas.addEventListener('touchstart', (e) => {
      this.startX = e.touches[0].clientX;
      this.stopAutoplay();
    });

    canvas.addEventListener('touchmove', (e) => handleMove(e.touches[0].clientX));

    canvas.addEventListener('touchend', () => {
      this.startX = null;
      this.accumulatedDelta = 0;
    });
  }

  toggleAutoplay(): void {
    if (this.isAutoplaying) {
      this.stopAutoplay();
    } else {
      this.startAutoplay();
    }
  }

  private updateProgress(): void {
    const progress = (this.currentFrame + 1) / this.frameCount;
    const newOffset = this.circumference * (1 - progress);

    if (Math.abs(this.progressOffset - newOffset) > 0.01) {
      this.progressOffset = newOffset;
      this.cdr.markForCheck();
    }
  }

  startAutoplay(): void {
    this.stopAutoplay();
    this.isAutoplaying = true;
    this.lastAutoplayTime = performance.now();

    const animate = (timestamp: number) => {
      if (!this.isAutoplaying) return;

      const elapsed = timestamp - this.lastAutoplayTime;
      if (elapsed >= this.autoplayInterval) {
        this.currentFrame = (this.currentFrame + 1) % this.frameCount;
        this.drawFrame(this.currentFrame);
        this.updateProgress();
        this.lastAutoplayTime = timestamp;
      }

      this.autoplayRafId = requestAnimationFrame(animate);
    };

    this.autoplayRafId = requestAnimationFrame(animate);
  }

  stopAutoplay(): void {
    if (this.autoplayRafId !== null) {
      cancelAnimationFrame(this.autoplayRafId);
      this.autoplayRafId = null;
    }
    this.isAutoplaying = false;
    this.cdr.detectChanges();
  }

  prevFrame(): void {
    this.stopAutoplay();
    this.clearHoldInterval();
    this.clearHoldTimeout();
    this.hasHeld = false;

    this.holdTimeout = setTimeout(() => {
      this.hasHeld = true;
      this.startHoldInterval(() => {
        this.currentFrame = (this.currentFrame - 1 + this.frameCount) % this.frameCount;
        this.drawFrame(this.currentFrame);
        this.updateProgress();
      });
    }, 75);

    setTimeout(() => {
      if (!this.hasHeld) {
        this.currentFrame = (this.currentFrame - 1 + this.frameCount) % this.frameCount;
        this.drawFrame(this.currentFrame);
        this.updateProgress();
      }
    }, 50);
  }

  nextFrame(): void {
    this.stopAutoplay();
    this.clearHoldInterval();
    this.clearHoldTimeout();
    this.hasHeld = false;

    this.holdTimeout = setTimeout(() => {
      this.hasHeld = true;
      this.startHoldInterval(() => {
        this.currentFrame = (this.currentFrame + 1) % this.frameCount;
        this.drawFrame(this.currentFrame);
        this.updateProgress();
      });
    }, 75);

    setTimeout(() => {
      if (!this.hasHeld) {
        this.currentFrame = (this.currentFrame + 1) % this.frameCount;
        this.drawFrame(this.currentFrame);
        this.updateProgress();
      }
    }, 50);
  }

  stopHold(): void {
    this.clearHoldInterval();
    this.clearHoldTimeout();
  }

  private clearHoldInterval(): void {
    if (this.holdInterval) {
      clearInterval(this.holdInterval);
      this.holdInterval = null;
    }
  }

  private clearHoldTimeout(): void {
    if (this.holdTimeout) {
      clearTimeout(this.holdTimeout);
      this.holdTimeout = null;
    }
  }

  private startHoldInterval(action: () => void): void {
    this.clearHoldInterval();
    this.holdInterval = setInterval(action, 150);
  }

  openModal(): void {
    this.openEmitter.emit();
  }
}
